/// <amd-module name="Artifacts/Feed/containers/FeedHubContainer" />

import { CreateFeedModalId } from "Artifacts/Feed/Components/CreateFeedPanel";
import { FeedHub } from "Artifacts/Feed/components/FeedHub";
import { usePackageList, useProblemPackages } from "Artifacts/Feed/hooks/PackageList";
import { FeedHubActions, FeedModule, IFeedModuleState } from "Artifacts/Feed/redux";
import { ProblemPackageItem } from "Artifacts/Feed/types/ProblemPackageItem";
import { ComponentState } from "Artifacts/Root/Constants";
import { SearchUpstreamsPanelId } from "Artifacts/Root/components/SearchUpstreamsPanel";
import { isUserAnonymousOrPublic } from "Artifacts/Root/helpers/UserHelpers";
import { getProtocolReduxModules } from "Artifacts/Root/protocols/Redux";
import { connectDynamicModules } from "Artifacts/Root/redux";
import { RootActions } from "Artifacts/Root/redux/Actions";
import { feedHubRoute } from "Artifacts/Root/routing/Constants";
import { feedHubContainerName } from "Artifacts/Root/routing/Definitions";
import { PackageListDataItem } from "Artifacts/Root/types/AzureArtifacts";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, useComponentContext } from "VSS/Platform/Layout";
import * as React from "react";
import { useMemo } from "react";
import { connect } from "react-redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";

const Actions = {
  loadPackages: FeedHubActions.loadPackageList,
  loadProblemPackages: FeedHubActions.loadProblemPackageList,
  showModal: RootActions.showModal,
  hideModal: RootActions.hideModal,
};

const FeedHubContainer: React.FC<IFeedHubContainerMapToStateProps & typeof Actions> = ({
  packageListComponentState,
  problemPackageListComponentState,
  problemPackageData,
  packageData,
  loadPackages,
  loadProblemPackages,
  showModal,
  hideModal,
}) => {
  const { pageContext } = useComponentContext();

  const {
    feed,
    feedResolution,
    view,
    filterBarProps,
    packageListSelection,
    scrollRef,
    isFilterActive,
    commands,
  } = usePackageList(loadPackages, feedHubRoute, packageData);
  const protocolReduxModules = useMemo(() => getProtocolReduxModules(pageContext), [pageContext]);
  const showCFSUI =
    // Not allowing public or anonmyous users to use the CFS UI.
    // TODO: Remove this line when CFS UI is available to all.
    // Will need to also change dataprovider attributes.
    !isUserAnonymousOrPublic(pageContext) &&
    isFeatureFlagEnabled(pageContext, "Packaging.Feed.ShowCFSUI", false);

  useProblemPackages(feed, showCFSUI, loadProblemPackages);

  const showCreateFeedModal = () => {
    showModal({
      modalId: CreateFeedModalId,
      props: { onDismiss: hideModal },
    });
  };

  const showSearchUpstreamsModal = () => {
    showModal({
      modalId: SearchUpstreamsPanelId,
      props: { onDismiss: hideModal },
    });
  };

  return (
    <DynamicModuleLoader modules={protocolReduxModules}>
      <FeedHub
        packageListComponentState={packageListComponentState}
        problemPackageListComponentState={problemPackageListComponentState}
        feed={feed}
        view={view}
        feedComponentState={feedResolution}
        problemPackageData={problemPackageData}
        packageData={packageData}
        packageListSelection={packageListSelection}
        commands={commands}
        filterBarProps={filterBarProps}
        isFilterActive={isFilterActive()}
        scrollRef={scrollRef}
        showCFSUI={showCFSUI}
        showCreateFeedModal={showCreateFeedModal}
        showSearchUpstreamsModal={showSearchUpstreamsModal}
      />
    </DynamicModuleLoader>
  );
};

interface IFeedHubContainerMapToStateProps {
  packageListComponentState: ComponentState;
  problemPackageListComponentState: ComponentState;
  packageData?: PackageListDataItem[];
  problemPackageData?: ProblemPackageItem[];
}

const mapStateToProps = (state: IFeedModuleState): IFeedHubContainerMapToStateProps => {
  const hubState = state["feed"].hub;
  return {
    packageListComponentState: hubState.packageListComponentState,
    problemPackageListComponentState: hubState.problemPackageListComponentState,
    packageData: hubState.packageData,
    problemPackageData: hubState.problemPackageData,
  };
};

const ConnectedFeedHubContainerComponent = connectDynamicModules(
  connect(mapStateToProps, Actions)(FeedHubContainer),
  FeedModule,
);

Components.add(feedHubContainerName, ConnectedFeedHubContainerComponent);

export { ConnectedFeedHubContainerComponent as FeedHubView };
